// client/src/pages/MenuPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { tokens } from '../styles/tokens';
import { ThemeProvider, useTheme } from '../context/ThemeContext';
import { useLanguage, localizeItem } from '../hooks/useLanguage';
import { LangSwitcher } from '../components/menu/LangSwitcher';
import { CategoryTabs } from '../components/menu/CategoryTabs';
import { MenuCard } from '../components/menu/MenuCard';
import { ItemModal } from '../components/menu/ItemModal';
import { CartBar } from '../components/cart/CartBar';
import { CartSheet } from '../components/cart/CartSheet';
import { normalizeCurrency, vkHref, maxMessengerHref } from '../utils/formatPrice';
import { ThemeToggleButton } from '../components/ThemeToggleButton';
import BrandFooter from '../components/BrandFooter';
import './MenuPage.css';

// ─── Корзина ──────────────────────────────────────────────────
function useCart() {
  const [items, setItems] = useState([]);
  const addItem = (menuItem) => {
    setItems((prev) => {
      const ex = prev.find((i) => i.id === menuItem.id);
      if (ex) return prev.map((i) => i.id === menuItem.id ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, { ...menuItem, quantity: 1 }];
    });
  };
  const removeItem = (id) => {
    setItems((prev) => {
      const ex = prev.find((i) => i.id === id);
      if (ex?.quantity === 1) return prev.filter((i) => i.id !== id);
      return prev.map((i) => i.id === id ? { ...i, quantity: i.quantity - 1 } : i);
    });
  };
  const clearCart   = () => setItems([]);
  const getQuantity = (id) => items.find((i) => i.id === id)?.quantity ?? 0;
  const totalItems  = items.reduce((s, i) => s + i.quantity, 0);
  const totalPrice  = items.reduce((s, i) => s + i.price * i.quantity, 0);
  return { items, addItem, removeItem, clearCart, getQuantity, totalItems, totalPrice };
}

// ─── Сетка категорий ──────────────────────────────────────────
function CategoryGrid({ categories, onSelect, lang }) {
  return (
    <div style={{ padding: '0 16px 120px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => onSelect(cat.id)}
            style={{
              position: 'relative',
              width: '100%',
              height: '140px',
              borderRadius: '20px',
              overflow: 'hidden',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              display: 'block',
            }}
          >
            {cat.image_url ? (
              <img
                src={cat.image_url}
                alt={cat.name_ru || cat.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            ) : (
              <div style={{
                width: '100%', height: '100%',
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
              }} />
            )}
            <div style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.15) 100%)',
            }} />
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{
                fontSize: '22px', fontWeight: '800', color: '#fff',
                textTransform: 'uppercase', letterSpacing: '0.5px',
                textShadow: '0 2px 8px rgba(0,0,0,0.6)',
                fontFamily: tokens.font.family,
                textAlign: 'center', padding: '0 16px',
              }}>
                {localizeItem(cat, lang)}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Внутренний компонент с доступом к теме ───────────────────
function MenuPageInner({ restaurantSlug: propSlug, tableNumber: propTable }) {
  const { slug: paramSlug } = useParams();
  const [searchParams] = useSearchParams();
  /** Без fallback 'demo' — иначе лишний /api/menu/demo (404) при пустом slug */
  const restaurantSlug = (propSlug ?? paramSlug ?? '').trim();
  const tableNumber = propTable ?? searchParams.get('table') ?? 1;

  const theme = useTheme();
  const { lang, setLang, t } = useLanguage();
  const [restaurant,     setRestaurant]     = useState(null);
  const [categories,     setCategories]     = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [selectedItem,   setSelectedItem]   = useState(null);
  const [showCart,       setShowCart]       = useState(false);
  const [loading,        setLoading]        = useState(true);
  const [fabExpanded,    setFabExpanded]    = useState(false);
  const [waiterCalled,   setWaiterCalled]   = useState(false);
  const [waiterLoading,  setWaiterLoading]  = useState(false);
  const fabCollapseTimer = useRef(null);

  const cart        = useCart();
  const sectionRefs = useRef({});
  const menuCurrency = normalizeCurrency(restaurant?.currency);

  useEffect(() => {
    if (!restaurantSlug) {
      setLoading(false);
      return;
    }
    axios.get(`/api/menu/${restaurantSlug}`)
      .then(({ data }) => {
        setRestaurant(data.restaurant);
        setCategories(data.categories || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [restaurantSlug]);

  useEffect(() => {
    if (restaurant?.name) document.title = `${restaurant.name} — Меню`;
  }, [restaurant?.name]);

  useEffect(() => {
    const enabled = restaurant?.enabled_languages;
    if (enabled?.length && !enabled.includes(lang)) {
      setLang(enabled[0]);
    }
  }, [restaurant?.enabled_languages, lang, setLang]);

  const handleFabTap = async () => {
    if (waiterCalled || waiterLoading) return;
    if (!fabExpanded) {
      setFabExpanded(true);
      clearTimeout(fabCollapseTimer.current);
      fabCollapseTimer.current = setTimeout(() => setFabExpanded(false), 4000);
      return;
    }
    clearTimeout(fabCollapseTimer.current);
    setFabExpanded(false);
    setWaiterLoading(true);
    try {
      await axios.post('/api/orders/call-waiter', {
        restaurantId: restaurant?.id,
        tableNumber,
        type: 'waiter',
      });
      setWaiterCalled(true);
      setTimeout(() => setWaiterCalled(false), 5000);
    } catch (err) {
      console.error('call waiter error:', err);
    } finally {
      setWaiterLoading(false);
    }
  };

  const scrollToCategory = (catId) => {
    const el = sectionRefs.current[catId];
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const handleCategorySelect = (catId) => {
    setActiveCategory(catId);
    setTimeout(() => scrollToCategory(catId), 100);
  };

  const handleTabSelect = (catId) => {
    setActiveCategory(catId);
    setTimeout(() => scrollToCategory(catId), 50);
  };

  const { colors, isDark } = theme;
  /* десктоп-подложка: заметно светлее #0A0A0A экрана, чтобы «рамка телефона» читалась */
  const desktopFrameBg = isDark ? '#3a3a44' : '#c8c7cc';

  if (loading) return (
    <div
      className="menu-page-viewport"
      style={{ backgroundColor: desktopFrameBg }}
    >
      <div
        className="menu-page-column"
        style={{
          backgroundColor: colors.bg,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ color: colors.textSecondary, fontSize: '15px' }}>{t.loading}</div>
      </div>
    </div>
  );

  if (!restaurantSlug) {
    return (
      <div
        className="menu-page-viewport"
        style={{ backgroundColor: desktopFrameBg }}
      >
        <div
          className="menu-page-column"
          style={{
            backgroundColor: colors.bg,
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            fontFamily: tokens.font.family,
            color: colors.text,
          }}
        >
          <div style={{ textAlign: 'center', maxWidth: '360px' }}>
            <p style={{ marginBottom: '16px', fontSize: '16px', lineHeight: 1.5 }}>
              Ресторан не указан в адресе. Откройте меню по ссылке вида{' '}
              <code style={{ fontSize: '13px' }}>/menu/ваш-slug</code> или перейдите на главную.
            </p>
            <Link
              to="/"
              style={{
                display: 'inline-block', padding: '12px 20px', borderRadius: '12px',
                background: '#111', color: '#fff', textDecoration: 'none', fontWeight: 700,
              }}
            >
              На главную
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="menu-page-viewport"
      style={{ backgroundColor: desktopFrameBg }}
    >
      <div
        className="menu-page-column"
        style={{
          backgroundColor: colors.bg,
          minHeight: '100vh',
          fontFamily: tokens.font.family,
          color: colors.text,
          transition: 'background-color 0.3s, color 0.3s',
        }}
      >

      {/* Кнопка переключения темы */}
      <ThemeToggleButton />

      {/* ── Шапка ── */}
      {restaurant && (
        <div>
          {restaurant.cover_url && (
            <div style={{ position: 'relative', height: '240px', overflow: 'hidden' }}>
              <img src={restaurant.cover_url} alt={restaurant.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              {theme.isDark && (
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0, height: '140px',
                  background: 'linear-gradient(to top, #0A0A0A 0%, transparent 100%)',
                }} />
              )}
            </div>
          )}

          <div style={{ padding: '0 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <h1 style={{
                fontSize: '24px', fontWeight: '700',
                color: (theme.isDark && restaurant.cover_url) ? '#fff' : colors.text,
                lineHeight: 1.1, position: 'relative', zIndex: 1,
                margin: restaurant.cover_url ? '16px 0 12px' : '20px 0 12px',
                flex: 1,
                textShadow: (theme.isDark && restaurant.cover_url) ? '0 2px 8px rgba(0,0,0,0.5)' : 'none',
              }}>
                {restaurant.name}
              </h1>
              <LangSwitcher
                lang={lang}
                setLang={setLang}
                enabledLanguages={restaurant.enabled_languages}
              />
            </div>

            {/* Инфо-карточка */}
            <div style={{
              backgroundColor: colors.bgCard,
              borderRadius: '18px', padding: '16px 18px',
              marginBottom: '14px', display: 'flex',
              flexDirection: 'column', gap: '16px',
              transition: 'background-color 0.3s',
            }}>
              {String(restaurant.description || '').trim() !== '' && (
                <div
                  style={{
                    fontSize: '14px',
                    color: colors.textSecondary,
                    lineHeight: 1.45,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {String(restaurant.description).trim()}
                </div>
              )}

              {restaurant.address && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <span style={{ fontSize: '20px', flexShrink: 0, marginTop: '2px' }}>📍</span>
                  <span style={{ fontSize: '14px', color: colors.textSecondary, lineHeight: 1.4, flex: 1 }}>
                    {restaurant.address}
                  </span>
                </div>
              )}

              {restaurant.phone && (
                <a href={`tel:${restaurant.phone}`}
                  style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '20px', flexShrink: 0 }}>📞</span>
                  <span style={{ fontSize: '14px', color: colors.textSecondary }}>{restaurant.phone}</span>
                </a>
              )}

              {restaurant.wifi_password && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                    <path d="M1 9l2 2c5-5 13-5 18 0l2-2C16.93 2.93 7.08 2.93 1 9z" fill={colors.textSecondary}/>
                    <path d="M5 13l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z" fill={colors.textSecondary}/>
                    <path d="M9 17l3 3 3-3c-1.65-1.66-4.34-1.66-6 0z" fill={colors.textSecondary}/>
                  </svg>
                  <span style={{ fontSize: '14px', color: colors.textSecondary }}>
                    Wi-Fi: {restaurant.wifi_password}
                  </span>
                </div>
              )}

              {(restaurant.instagram || restaurant.telegram || restaurant.whatsapp
                || restaurant.vk_url || restaurant.max_messenger_url) && (
                <div style={{
                  paddingTop: '16px', marginTop: '2px', borderTop: `1px solid ${colors.border}`,
                  display: 'flex', flexDirection: 'row', gap: '10px', flexWrap: 'wrap',
                  alignItems: 'center',
                }}>
                  {restaurant.instagram && (
                    <a href={`https://instagram.com/${restaurant.instagram.replace('@', '')}`}
                      target="_blank" rel="noreferrer"
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '10px 16px', borderRadius: '100px',
                        backgroundColor: colors.bgChip, textDecoration: 'none',
                        flex: 1, minWidth: '120px', justifyContent: 'center',
                      }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                        <defs>
                          <linearGradient id="ig1" x1="0%" y1="100%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#F9CE34"/>
                            <stop offset="33%" stopColor="#EE2A7B"/>
                            <stop offset="66%" stopColor="#6228D7"/>
                          </linearGradient>
                        </defs>
                        <rect x="2" y="2" width="20" height="20" rx="6" fill="url(#ig1)"/>
                        <circle cx="12" cy="12" r="4.5" stroke="white" strokeWidth="1.8" fill="none"/>
                        <circle cx="17.5" cy="6.5" r="1.2" fill="white"/>
                      </svg>
                      <span style={{ fontSize: '13px', color: colors.text, fontWeight: '500' }}>
                        {restaurant.instagram}
                      </span>
                    </a>
                  )}

                  {restaurant.telegram && (
                    <a href={`https://t.me/${restaurant.telegram.replace('@', '')}`}
                      target="_blank" rel="noreferrer"
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '10px 16px', borderRadius: '100px',
                        backgroundColor: colors.bgChip, textDecoration: 'none',
                        flex: 1, minWidth: '120px', justifyContent: 'center',
                      }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                        <circle cx="12" cy="12" r="10" fill="#29B6F6"/>
                        <path d="M5 11.5l4.5 1.8 1.8 4.7 1.7-3 3.5-7.5L5 11.5z" fill="white"/>
                      </svg>
                      <span style={{ fontSize: '13px', color: colors.text, fontWeight: '500' }}>
                        {restaurant.telegram}
                      </span>
                    </a>
                  )}

                  {restaurant.whatsapp && (
                    <a href={`https://wa.me/${restaurant.whatsapp.replace(/\D/g, '')}`}
                      target="_blank" rel="noreferrer"
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '10px 16px', borderRadius: '100px',
                        backgroundColor: colors.bgChip, textDecoration: 'none',
                        flex: 1, minWidth: '120px', justifyContent: 'center',
                      }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                        <circle cx="12" cy="12" r="10" fill="#25D366"/>
                        <path d="M17 14.9c-.2-.1-1.4-.7-1.6-.8-.2-.1-.4-.1-.6.1-.2.2-.6.8-.8 1-.1.2-.3.2-.5.1-.7-.3-1.4-.7-2-1.2-.5-.5-1-1.1-1.4-1.7-.1-.2 0-.4.1-.5l.4-.5c.1-.1.2-.3.2-.4 0-.1 0-.3-.1-.4L9.9 9c-.2-.4-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3C7.9 9.1 7.5 9.6 7.5 10.6c0 1 .7 2 .8 2.1.1.1 1.4 2.2 3.4 3 1.9.8 1.9.5 2.3.5.4 0 1.2-.5 1.4-1 .2-.5.2-.9.1-1-.1-.1-.2-.1-.5-.2z" fill="white"/>
                      </svg>
                      <span style={{ fontSize: '13px', color: colors.text, fontWeight: '500' }}>
                        {restaurant.whatsapp}
                      </span>
                    </a>
                  )}

                  {restaurant.vk_url && vkHref(restaurant.vk_url) && (
                    <a href={vkHref(restaurant.vk_url)}
                      target="_blank" rel="noreferrer"
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '10px 16px', borderRadius: '100px',
                        backgroundColor: colors.bgChip, textDecoration: 'none',
                        flex: 1, minWidth: '120px', justifyContent: 'center',
                      }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                        <rect width="24" height="24" rx="4" fill="#0077FF"/>
                        <path d="M12.65 16.5c-5.2 0-8.18-3.57-8.32-9.5h2.58c.11 4.13 1.9 5.88 3.35 6.24V7h2.43v3.57c1.45-.16 2.93-1.8 3.44-3.57h2.43c-.39 2.45-2.05 4.1-3.22 4.66 1.17.54 2.72 2.1 3.35 4.84h-2.43c-.5-1.55-1.66-2.75-3.57-2.9V16.5h-2.9z" fill="#fff"/>
                      </svg>
                      <span style={{ fontSize: '13px', color: colors.text, fontWeight: '500' }}>VK</span>
                    </a>
                  )}

                  {restaurant.max_messenger_url && maxMessengerHref(restaurant.max_messenger_url) && (
                    <a href={maxMessengerHref(restaurant.max_messenger_url)}
                      target="_blank" rel="noreferrer"
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '10px 16px', borderRadius: '100px',
                        backgroundColor: colors.bgChip, textDecoration: 'none',
                        flex: 1, minWidth: '120px', justifyContent: 'center',
                      }}>
                      <span style={{
                        fontSize: '11px', fontWeight: '800', color: '#fff',
                        background: 'linear-gradient(135deg,#6C5CE7,#A29BFE)',
                        width: '22px', height: '22px', borderRadius: '6px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>M</span>
                      <span style={{ fontSize: '13px', color: colors.text, fontWeight: '500' }}>MAX</span>
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Сетка категорий или список блюд ── */}
      {activeCategory === null ? (
        <>
          <div style={{
            display: 'flex', gap: '8px', overflowX: 'auto',
            padding: '0 16px 16px',
            scrollbarWidth: 'none',
          }}>
            <button
              onClick={() => {
                setActiveCategory(null);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              style={{
                flexShrink: 0, padding: '8px 18px',
                borderRadius: '100px', border: 'none',
                backgroundColor: activeCategory === null
                  ? (theme.isDark ? '#fff' : '#1A1A1A')
                  : colors.bgChip,
                color: activeCategory === null
                  ? (theme.isDark ? '#000' : '#fff')
                  : colors.textSecondary,
                fontSize: '12px', fontWeight: '600',
                whiteSpace: 'nowrap', cursor: 'pointer',
                fontFamily: tokens.font.family,
                transition: 'background-color 0.2s',
              }}
            >
              {t.menu}
            </button>
            {categories.map((cat) => (
              <button key={cat.id} onClick={() => handleCategorySelect(cat.id)}
                style={{
                  flexShrink: 0, padding: '8px 18px',
                  borderRadius: '100px', border: 'none',
                  backgroundColor: colors.bgChip,
                  color: colors.textSecondary,
                  fontSize: '12px', fontWeight: '500',
                  cursor: 'pointer', whiteSpace: 'nowrap',
                  fontFamily: tokens.font.family,
                  transition: 'background-color 0.2s',
                }}>
                {localizeItem(cat, lang)}
              </button>
            ))}
          </div>

          <CategoryGrid categories={categories} onSelect={handleCategorySelect} lang={lang} />
        </>
      ) : (
        <>
          <CategoryTabs
            categories={categories}
            activeId={activeCategory}
            onSelect={handleTabSelect}
            lang={lang}
            menuLabel={t.menu}
          />

          <div style={{ padding: '0 16px', paddingBottom: '120px' }}>
            <button
              onClick={() => { setActiveCategory(null); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '8px 0', marginBottom: '16px',
                background: 'none', border: 'none', cursor: 'pointer',
                color: colors.textSecondary, fontSize: '14px',
                fontFamily: tokens.font.family,
              }}
            >
              {t.allCategories}
            </button>

            {categories.map((cat) => {
              const catItems = cat.items || [];
              if (catItems.length === 0) return null;
              return (
                <div key={cat.id}
                  ref={(el) => { sectionRefs.current[cat.id] = el; }}
                  style={{ marginBottom: '32px' }}>
                  <h2 style={{
                    margin: '0 0 14px',
                    fontSize: '22px',
                    fontWeight: '800',
                    color: colors.text,
                  }}>
                    {localizeItem(cat, lang)}
                  </h2>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    {catItems.map((item) => (
                      <MenuCard
                        key={item.id}
                        item={item}
                        lang={lang}
                        t={t}
                        currency={menuCurrency}
                        quantity={cart.getQuantity(item.id)}
                        onAdd={cart.addItem}
                        onClick={setSelectedItem}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── Баннер «Официант уже идёт» ── */}
      {waiterCalled && (
        <div style={{
          position: 'fixed',
          bottom: 'calc(24px + env(safe-area-inset-bottom))',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 99,
          backgroundColor: '#1A3A2A',
          border: '1px solid #34C759',
          color: '#34C759',
          borderRadius: '100px',
          padding: '12px 22px',
          fontSize: '15px',
          fontWeight: '700',
          whiteSpace: 'nowrap',
          boxShadow: '0 4px 20px rgba(52,199,89,0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontFamily: tokens.font.family,
          animation: 'fadeInUp 0.25s ease',
        }}>
          🔔 Официант уже идёт!
        </div>
      )}

      {/* ── FAB: портал в body — иначе на десктопе transform колонки прячет кнопку внизу скролла */}
      {cart.totalItems === 0 && !waiterCalled
        && createPortal(
        <button
          onClick={handleFabTap}
          disabled={waiterLoading}
          aria-label="Вызвать официанта"
          style={{
            position: 'fixed',
            bottom: 'calc(24px + env(safe-area-inset-bottom))',
            right: 'max(16px, env(safe-area-inset-right, 0px))',
            zIndex: 5001,
            height: '52px',
            borderRadius: '100px',
            border: theme.isDark
              ? '1px solid rgba(255,255,255,0.1)'
              : '1px solid rgba(0,0,0,0.1)',
            backgroundColor: fabExpanded
              ? (theme.isDark ? '#2A2A3E' : '#E8E8F0')
              : (theme.isDark ? '#1E1E2E' : '#FFFFFF'),
            boxShadow: theme.isDark
              ? '0 4px 16px rgba(0,0,0,0.4)'
              : '0 4px 16px rgba(0,0,0,0.12)',
            cursor: waiterLoading ? 'not-allowed' : 'pointer',
            fontSize: '22px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: fabExpanded ? '8px' : '0',
            padding: fabExpanded ? '0 20px' : '0',
            width: fabExpanded ? 'auto' : '52px',
            transition: 'width 0.25s ease, padding 0.25s ease, background-color 0.2s',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            fontFamily: tokens.font.family,
          }}
        >
          {waiterLoading ? '⏳' : '🛎️'}
          {fabExpanded && (
            <span style={{
              color: colors.text,
              fontSize: '14px',
              fontWeight: '600',
            }}>
              Вызвать официанта
            </span>
          )}
        </button>,
        document.body,
        )}


      {/* ── Корзина ── */}
      <CartBar
        itemCount={cart.totalItems}
        total={cart.totalPrice}
        currency={menuCurrency}
        onOpen={() => setShowCart(true)}
        onWaiter={() => setShowCart(true)}
      />

      {selectedItem && (
        <ItemModal
          item={selectedItem}
          lang={lang}
          t={t}
          currency={menuCurrency}
          quantity={cart.getQuantity(selectedItem.id)}
          onClose={() => setSelectedItem(null)}
          onAdd={cart.addItem}
          onRemove={cart.removeItem}
        />
      )}

      {showCart && (
        <CartSheet
          items={cart.items}
          restaurantId={restaurant?.id}
          tableNumber={tableNumber}
          t={t}
          lang={lang}
          currency={menuCurrency}
          onClose={() => setShowCart(false)}
          onClear={cart.clearCart}
          onAdd={cart.addItem}
          onRemove={cart.removeItem}
        />
      )}

      <BrandFooter linkColor={colors.text} />
      </div>
    </div>
  );
}

// ─── Экспортируемый компонент с провайдером темы ──────────────
export default function MenuPage(props) {
  return (
    <ThemeProvider>
      <MenuPageInner {...props} />
    </ThemeProvider>
  );
}
